import {
    AmountString,
    GenericState,
    LinearId,
    MOCK_CONTRACTOR,
    MOCK_DEVELOPER,
    UploadedFile,
    HistoryItem,
} from "./common";
import { PaymentInfo, PaymentOrderStatus, PaymentStatus } from "./payment";

export type InvoiceProps = {
    claimLinearId: LinearId;
    bankAccountDetails: BankAccountDetails;
    amount: AmountString;
    documents: UploadedFile[];
    developer: any;
    mainContractor: any;
    paymentIds: LinearId[];
};

export type BankAccountDetails = {
    id?: string;
    bankAccountHolderName: string;
    bankAccountNumber?: number | null;
    bankAccountNumberStr: string;
    swiftAddress: string;
    bankName: string;
    registeredAddress: string;
    currency: string;
}
export type InvoiceState = GenericState<InvoiceProps, InvoiceStatus>;

export type InvoiceStateWithPaymentInfo = {
    invoiceState: InvoiceState;
    paymentInfo: PaymentInfo | null;
};

export type InvoiceStatusWithPaymentInfo = {
    invoiceStatus: InvoiceStatus;
    paymentInfo: PaymentInfo | null;
};

export type InvoiceSummary = {
    currency: string | null;
    totalPaidAmount: number;
    totalPendingAmount: number;
};

export type InvoiceHistory = {
    invoiceId: string;
    currentStatus: InvoiceStatus;
    statusHistory: HistoryItem<InvoiceStatus>[];
};

export enum InvoiceStatus {
    CONTRACTOR_SUBMITTED = "SUBMITTED",
    CONTRACTOR_DISCARDED = "DISCARDED",
    PO_APPROVED = "PROJECT_OFFICER_APPROVED",
    PO_REJECTED = "PROJECT_OFFICER_REJECTED",
    FINANCE_APPROVED = "FINANCE_DEPARTMENT_APPROVED",
    FINANCE_REJECTED = "FINANCE_DEPARTMENT_REJECTED",
    DELETED = "DELETED",
};

const MOCK_INVOICE_PROPS: InvoiceProps = {
    claimLinearId: {
        id: "some-id",
    },
    bankAccountDetails: {
        id: "9183eu-12ewfdiujn2e-21ewfd-1w",
        bankAccountHolderName: "John Doe",
        bankAccountNumber: null,
        bankAccountNumberStr: "123213213",
        swiftAddress: "ABCD",
        bankName: "The bank of bank bank",
        registeredAddress: "somewhere over the rainbow",
        currency: "HKD",
    },
    amount: "100000 SGD",
    documents: [
        {
            name: "some-file-1.pdf",
            id: "some-hash-1",
        },
        {
            name: "some-file-2.pdf",
            id: "some-hash-2",
        },
    ],
    developer: MOCK_DEVELOPER.party,
    mainContractor: MOCK_CONTRACTOR[0].party,
    paymentIds: [],
};

export const MOCK_INVOICE_LIST: InvoiceStateWithPaymentInfo[] = [
    {
        invoiceState: {
            linearId: {
                id: "uuid-1",
            },
            createdAt: "2021-11-02T04:18:43.299Z",
            status: InvoiceStatus.CONTRACTOR_SUBMITTED,
            props: MOCK_INVOICE_PROPS,
            lastChangedTime: "2021-11-02T04:18:43.299Z",
            comments: [],
        },
        paymentInfo: null,
    },
    {
        invoiceState: {
            linearId: {
                id: "uuid-2",
            },
            createdAt: "2021-11-02T04:18:43.299Z",
            status: InvoiceStatus.CONTRACTOR_DISCARDED,
            props: MOCK_INVOICE_PROPS,
            lastChangedTime: "2021-11-02T04:18:43.299Z",
            comments: [],
        },
        paymentInfo: null,
    },
    {
        invoiceState: {
            linearId: {
                id: "uuid-3",
            },
            createdAt: "2021-11-02T04:18:43.299Z",
            status: InvoiceStatus.PO_APPROVED,
            props: MOCK_INVOICE_PROPS,
            lastChangedTime: "2021-11-02T04:18:43.299Z",
            comments: [],
        },
        paymentInfo: null,
    },
    {
        invoiceState: {
            linearId: {
                id: "uuid-4",
            },
            createdAt: "2021-11-02T04:18:43.299Z",
            status: InvoiceStatus.PO_REJECTED,
            props: MOCK_INVOICE_PROPS,
            lastChangedTime: "2021-11-02T04:18:43.299Z",
            comments: [
                {
                    fullName: "ca name",
                    email: "ca@ca.com",
                    comment: "PO rejected",
                    resolved: false,
                    created: "2023-06-21T10:07:12.646Z",
                },
            ],
        },
        paymentInfo: null,
    },
    {
        invoiceState: {
            linearId: {
                id: "uuid-5",
            },
            createdAt: "2021-11-02T04:18:43.299Z",
            status: InvoiceStatus.FINANCE_APPROVED,
            props: MOCK_INVOICE_PROPS,
            lastChangedTime: "2021-11-02T04:18:43.299Z",
            comments: [],
        },
        paymentInfo: null,
    },
    {
        invoiceState: {
            linearId: {
                id: "uuid-6",
            },
            createdAt: "2021-11-02T04:18:43.299Z",
            status: InvoiceStatus.FINANCE_REJECTED,
            props: MOCK_INVOICE_PROPS,
            lastChangedTime: "2021-11-02T04:18:43.299Z",
            comments: [
                {
                    fullName: "ca name",
                    email: "ca@ca.com",
                    comment: "finance rejected",
                    resolved: false,
                    created: "2023-06-21T10:07:12.646Z",
                },
            ],
        },
        paymentInfo: null,
    },
    {
        invoiceState: {
            linearId: {
                id: "uuid-7",
            },
            createdAt: "2021-11-02T04:18:43.299Z",
            status: InvoiceStatus.FINANCE_APPROVED,
            props: MOCK_INVOICE_PROPS,
            lastChangedTime: "2021-11-02T04:18:43.299Z",
            comments: [],
        },
        paymentInfo: {
            paymentOrderApprovers: [
                {
                    details: {
                        roleName: "r2",
                        firstName: "Minh",
                        lastName: "Payment",
                        email: "minhpaymentorder@gmail.com",
                        userProfileName: "Approver Entity",
                    },
                    actionTimestamp: "2023-06-15T08:27:11.255Z",
                    status: undefined,
                },
            ],
            paymentOrderStatus: PaymentOrderStatus.CREATED,
            paymentStatus: null,
        },
    },
    {
        invoiceState: {
            linearId: {
                id: "uuid-8",
            },
            createdAt: "2021-11-02T04:18:43.299Z",
            status: InvoiceStatus.FINANCE_APPROVED,
            props: MOCK_INVOICE_PROPS,
            lastChangedTime: "2021-11-02T04:18:43.299Z",
            comments: [],
        },
        paymentInfo: {
            paymentOrderApprovers: [
                {
                    details: {
                        roleName: "r2",
                        firstName: "Minh",
                        lastName: "Payment",
                        email: "minhpaymentorder@gmail.com",
                        userProfileName: "Approver Entity",
                    },
                    actionTimestamp: "2023-06-15T08:27:11.255Z",
                    status: undefined,
                },
            ],
            paymentOrderStatus: PaymentOrderStatus.PARTIAL_APPROVED,
            paymentStatus: PaymentStatus.SUBMITTED,
        },
    },
    {
        invoiceState: {
            linearId: {
                id: "uuid-9",
            },
            createdAt: "2021-11-02T04:18:43.299Z",
            status: InvoiceStatus.FINANCE_APPROVED,
            props: MOCK_INVOICE_PROPS,
            lastChangedTime: "2021-11-02T04:18:43.299Z",
            comments: [],
        },
        paymentInfo: {
            paymentOrderApprovers: [
                {
                    details: {
                        roleName: "r2",
                        firstName: "Minh",
                        lastName: "Payment",
                        email: "minhpaymentorder@gmail.com",
                        userProfileName: "Approver Entity",
                    },
                    actionTimestamp: "2023-06-15T08:27:11.255Z",
                    status: undefined,
                },
            ],
            paymentOrderStatus: PaymentOrderStatus.PARTIAL_APPROVED,
            paymentStatus: PaymentStatus.COMPLETED,
        },
    },
    {
        invoiceState: {
            linearId: {
                id: "uuid-10",
            },
            createdAt: "2021-11-02T04:18:43.299Z",
            status: InvoiceStatus.FINANCE_APPROVED,
            props: MOCK_INVOICE_PROPS,
            lastChangedTime: "2021-11-02T04:18:43.299Z",
            comments: [],
        },
        paymentInfo: {
            paymentOrderApprovers: [
                {
                    details: {
                        roleName: "r2",
                        firstName: "Minh",
                        lastName: "Payment",
                        email: "minhpaymentorder@gmail.com",
                        userProfileName: "Approver Entity",
                    },
                    actionTimestamp: "2023-06-15T08:27:11.255Z",
                    status: undefined,
                },
            ],
            paymentOrderStatus: PaymentOrderStatus.PARTIAL_APPROVED,
            paymentStatus: PaymentStatus.FAILED,
        },
    },
    {
        invoiceState: {
            linearId: {
                id: "uuid-11",
            },
            createdAt: "2021-11-02T04:18:43.299Z",
            status: InvoiceStatus.FINANCE_APPROVED,
            props: MOCK_INVOICE_PROPS,
            lastChangedTime: "2021-11-02T04:18:43.299Z",
            comments: [],
        },
        paymentInfo: {
            paymentOrderApprovers: [
                {
                    details: {
                        roleName: "r2",
                        firstName: "Minh",
                        lastName: "Payment",
                        email: "minhpaymentorder@gmail.com",
                        userProfileName: "Approver Entity",
                    },
                    actionTimestamp: "2023-06-15T08:27:11.255Z",
                    status: undefined,
                },
            ],
            paymentOrderStatus: PaymentOrderStatus.REJECTED,
            paymentStatus: null,
        },
    },
    {
        invoiceState: {
            linearId: {
                id: "uuid-12",
            },
            createdAt: "2021-11-02T04:18:43.299Z",
            status: InvoiceStatus.FINANCE_REJECTED,
            props: MOCK_INVOICE_PROPS,
            lastChangedTime: "2021-11-02T04:18:43.299Z",
            comments: [
                {
                    fullName: "ca name",
                    email: "ca@ca.com",
                    comment: "finance rejected",
                    resolved: false,
                    created: "2023-06-21T10:07:12.646Z",
                },
            ],
        },
        paymentInfo: {
            paymentOrderApprovers: [
                {
                    details: {
                        roleName: "r2",
                        firstName: "Minh",
                        lastName: "Payment",
                        email: "minhpaymentorder@gmail.com",
                        userProfileName: "Approver Entity",
                    },
                    actionTimestamp: "2023-06-15T08:27:11.255Z",
                    status: undefined,
                },
            ],
            paymentOrderStatus: PaymentOrderStatus.PARTIAL_APPROVED,
            paymentStatus: PaymentStatus.FAILED,
        },
    },
];

export const MOCK_COMPLETED_INVOICE = MOCK_INVOICE_LIST[6];

export const MOCK_INVOICE_HISTORY_ITEMS: HistoryItem<InvoiceStatus>[] = [
    {
        status: InvoiceStatus.CONTRACTOR_SUBMITTED,
        timestamp: "2026-07-13T05:14:19Z",
    },
    {
        status: InvoiceStatus.PO_APPROVED,
        timestamp: "2026-07-13T05:16:27Z",
    },
    {
        status: InvoiceStatus.FINANCE_APPROVED,
        timestamp: "2026-07-13T05:20:51Z",
    }
];

export const MOCK_INVOICE_HISTORY: InvoiceHistory = {
    invoiceId: "9",
    currentStatus: InvoiceStatus.FINANCE_APPROVED,
    statusHistory: MOCK_INVOICE_HISTORY_ITEMS,
};

export const MOCK_INVOICE_SUMMARY: InvoiceSummary = {
    currency: "SGD",
    totalPaidAmount: 100000,
    totalPendingAmount: 10000.3,
};
